import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiEdit2, FiPlus, FiSearch } from "react-icons/fi";
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import { employeeService } from "../../services/employee.service";

const STATUS_STYLES = {
  Active: "bg-green-100 text-green-700",
  "On Leave": "bg-orange-100 text-orange-600",
  Inactive: "bg-red-100 text-red-600",
};

const ALL_OPTION = { value: "All", label: "All" };

// A filter with parentKey only lists options that belong to the selected parent.
const FILTER_DEFINITIONS = [
  { key: "department", label: "Department", optionsKey: "departments", idField: "department_id", nameField: "department_name", rowField: "departmentId" },
  { key: "subDepartment", label: "Sub Department", optionsKey: "subDepartments", idField: "sub_department_id", nameField: "sub_department_name", rowField: "subDepartmentId", parentKey: "department", parentField: "department_id" },
  { key: "jobLocation", label: "Job Location", optionsKey: "jobLocations", idField: "job_location_id", nameField: "job_location_name", rowField: "jobLocationId" },
  { key: "employeeType", label: "Employee Type", optionsKey: "employeeTypes", idField: "employee_type_id", nameField: "employee_type_name", rowField: "employeeTypeId" },
  { key: "employeeCategory", label: "Employee Category", optionsKey: "employeeCategories", idField: "employee_category_id", nameField: "employee_category_name", rowField: "employeeCategoryId", parentKey: "employeeType", parentField: "employee_type_id" },
  { key: "status", label: "Status", optionsKey: "statuses", idField: "status_id", nameField: "status_name", rowField: "statusId" },
];

const EMPTY_FILTERS = Object.fromEntries(FILTER_DEFINITIONS.map((filter) => [filter.key, "All"]));
const EMPTY_FILTER_OPTIONS = Object.fromEntries(FILTER_DEFINITIONS.map((filter) => [filter.optionsKey, []]));
const EMPTY_SUMMARY = { totalEmployee: 0, activeEmployee: 0, inactiveEmployee: 0, percentageOfLeave: 0 };

const PAGE_SIZES = [5, 10, 20];

// Every column sorts. `value` returns what the sort compares.
const COLUMNS = [
  { key: "id", label: "Id", value: (employee) => employee.id },
  { key: "name", label: "Name", value: (employee) => employee.name },
  { key: "department", label: "Department", value: (employee) => employee.departmentName },
  { key: "designation", label: "Designation", value: (employee) => employee.designationName },
  { key: "location", label: "Location", value: (employee) => employee.jobLocationName },
  { key: "employeeType", label: "Employee Type", value: (employee) => employee.employeeTypeName },
  { key: "dateOfJoin", label: "Date of Join", value: (employee) => employee.dateOfJoinRaw, isDate: true },
  { key: "status", label: "Status", value: (employee) => employee.statusName },
];

function compareValues(a, b, isDate) {
  if (isDate) {
    const left = Date.parse(a || "");
    const right = Date.parse(b || "");
    if (Number.isNaN(left)) return Number.isNaN(right) ? 0 : -1;
    if (Number.isNaN(right)) return 1;
    return left - right;
  }
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true, sensitivity: "base" });
}

function FilterSelect({ label, value, options, onChange }) {
  const selected = options.find((option) => option.value === value);
  return (
    <label className="relative flex h-[34px] items-center rounded-md border border-gray-200 bg-white pl-3 pr-7 text-xs text-gray-500">
      <span className="whitespace-nowrap">{label}:</span>
      <span className="ml-1 max-w-[120px] truncate font-semibold text-gray-800">{selected?.label ?? value}</span>
      <MdKeyboardArrowDown className="pointer-events-none absolute right-1.5 h-4 w-4 text-gray-500" />
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        aria-label={label}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function SortArrows({ direction }) {
  return (
    <span className="ml-1 inline-flex flex-col leading-none">
      <span className={`h-0 w-0 border-x-[4px] border-b-[5px] border-x-transparent ${direction === "asc" ? "border-b-gray-800" : "border-b-gray-400"}`} />
      <span className={`mt-[2px] h-0 w-0 border-x-[4px] border-t-[5px] border-x-transparent ${direction === "desc" ? "border-t-gray-800" : "border-t-gray-400"}`} />
    </span>
  );
}

function PagerButton({ onClick, disabled, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex h-7 w-7 items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

function HUmanResourceDashboard() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const moduleBase = `/${pathname.split("/")[1]}/Human-Resource`;

  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ key: "dateOfJoin", direction: "desc" });
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    document.title = "Employee Management";
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadEmployees() {
    setLoading(true);
    setError("");
    try {
      const result = await employeeService.getEmployees();
      setEmployees(result.employees);
      setSummary(result.summary);
      setFilterOptions(result.filterOptions);
    } catch (loadError) {
      setError(loadError?.message || "Unable to load employees.");
    } finally {
      setLoading(false);
    }
  }

  function filterChoices(filter) {
    return [
      ALL_OPTION,
      ...filterOptions[filter.optionsKey]
        .filter(
          (option) =>
            !filter.parentKey ||
            filters[filter.parentKey] === "All" ||
            String(option[filter.parentField]) === filters[filter.parentKey]
        )
        .map((option) => ({
        value: String(option[filter.idField]),
        label: option[filter.nameField],
      })),
    ];
  }

  const filtered = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    const rows = employees.filter((employee) => {
      const haystack = `${employee.name} ${employee.id} ${employee.email}`;
      return (
        haystack.toLowerCase().includes(search) &&
        FILTER_DEFINITIONS.every(
          (filter) =>
            filters[filter.key] === "All" ||
            String(employee[filter.rowField]) === filters[filter.key]
        )
      );
    });
    const column = COLUMNS.find((item) => item.key === sort.key);
    rows.sort((a, b) => {
      const diff = compareValues(column.value(a), column.value(b), column.isDate);
      return sort.direction === "asc" ? diff : -diff;
    });
    return rows;
  }, [employees, searchText, filters, sort]);

  function toggleSort(key) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
    setPage(1);
  }

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function resetPage(setter) {
    return (value) => {
      setter(value);
      setPage(1);
    };
  }

  return (
    <div className="@container w-full min-w-0">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-gray-500" aria-label="Breadcrumb">
        <Link to={`/${pathname.split("/")[1]}/dashboard`} className="hover:text-blue-500">
          Home
        </Link>
        <span>/</span>
        <Link to={`${moduleBase}/dashboard`} className="hover:text-blue-500">
          Human Resource
        </Link>
        <span>/</span>
        <span className="font-medium text-blue-500">Employee Management</span>
      </nav>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Employee Management</h1>
        <button
          type="button"
          onClick={() => navigate(`${moduleBase}/employees/new`)}
          className="flex h-[38px] items-center gap-2 rounded-md border border-blue-500 bg-white px-4 text-sm font-medium text-blue-500 hover:bg-blue-50"
        >
          <FiPlus className="h-4 w-4" />
          Add New Employee
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 @sm:grid-cols-2 @4xl:grid-cols-4">
        {[
          { label: "Total Employees", value: Number(summary.totalEmployee).toLocaleString() },
          { label: "Active Employees", value: Number(summary.activeEmployee).toLocaleString() },
          { label: "Inactive Employees", value: Number(summary.inactiveEmployee).toLocaleString() },
          { label: "Employee on Leave", value: `${summary.percentageOfLeave}%` },
        ].map((card) => (
          <div key={card.label} className="rounded-lg border border-gray-200 bg-slate-50 px-4 py-3">
            <p className="text-xs text-gray-600">{card.label}</p>
            <p className="mt-2 text-xl font-bold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="relative mt-5 w-full @lg:w-[320px]">
        <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-500" />
        <input
          value={searchText}
          onChange={(event) => resetPage(setSearchText)(event.target.value)}
          placeholder="Search by name, ID or email"
          className="h-[34px] w-full rounded-md border border-gray-200 bg-slate-50 pl-9 pr-3 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {FILTER_DEFINITIONS.map((filter) => (
            <FilterSelect
              key={filter.key}
              label={filter.label}
              value={filters[filter.key]}
              options={filterChoices(filter)}
              onChange={(value) => {
                setFilters((current) => {
                  const next = { ...current, [filter.key]: value };
                  FILTER_DEFINITIONS.forEach((child) => {
                    if (child.parentKey === filter.key) next[child.key] = "All";
                  });
                  return next;
                });
                setPage(1);
              }}
            />
          ))}
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[1040px] text-left text-xs">
            <thead>
              <tr className="bg-slate-100 text-gray-800">
                {COLUMNS.map((column, index) => (
                  <th
                    key={column.key}
                    className={`px-3 py-3 font-semibold ${index === 0 ? "rounded-l-md" : ""}`}
                    aria-sort={sort.key === column.key ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="inline-flex items-center whitespace-nowrap font-semibold"
                    >
                      {column.label}
                      <SortArrows direction={sort.key === column.key ? sort.direction : null} />
                    </button>
                  </th>
                ))}
                <th className="rounded-r-md px-3 py-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading || error || pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 1}
                    className={`px-3 py-10 text-center text-sm ${error ? "text-red-700" : "text-gray-500"}`}
                  >
                    {loading ? "Loading employees..." : error || "No employees found."}
                  </td>
                </tr>
              ) : (
                pageRows.map((employee) => {
                  return (
                    <tr
                      key={employee.id}
                      onClick={() => navigate(`${moduleBase}/employees/${employee.id}`)}
                      className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-slate-50"
                    >
                      <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-800">{employee.id}</td>
                      <td className="px-3 py-3">
                        <Link
                          to={`${moduleBase}/employees/${employee.id}`}
                          className="font-medium text-gray-900 hover:text-blue-500"
                        >
                          {employee.name}
                        </Link>
                        <p className="max-w-[170px] truncate text-[11px] text-blue-500">{employee.email}</p>
                      </td>
                      <td className="px-3 py-3 text-gray-800">{employee.departmentName}</td>
                      <td className="px-3 py-3 text-gray-800">{employee.designationName}</td>
                      <td className="px-3 py-3 text-gray-800">{employee.jobLocationName}</td>
                      <td className="whitespace-nowrap px-3 py-3 text-gray-800">{employee.employeeTypeName}</td>
                      <td className="whitespace-nowrap px-3 py-3 text-gray-900">{employee.dateOfJoin}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-block whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-medium ${STATUS_STYLES[employee.statusName] || "bg-gray-100 text-gray-600"}`}>
                          {employee.statusName}
                        </span>
                      </td>
                      <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                        <div className="flex items-center justify-center gap-3">
                          <button
                            type="button"
                            onClick={() => navigate(`${moduleBase}/employees/${employee.id}/edit`)}
                            aria-label={`Edit ${employee.name}`}
                            className="text-gray-700 hover:text-blue-500"
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
          <p>{filtered.length} row(s)</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                className="h-8 w-16 rounded border border-gray-200 bg-slate-50 px-2 text-xs text-gray-800 focus:outline-none"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <span>
              Page {currentPage} of {pageCount}
            </span>
            <div className="flex items-center">
              <PagerButton label="First page" onClick={() => setPage(1)} disabled={currentPage === 1}>
                <MdKeyboardDoubleArrowLeft className="h-4 w-4" />
              </PagerButton>
              <PagerButton label="Previous page" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
                <MdKeyboardArrowLeft className="h-4 w-4" />
              </PagerButton>
              <PagerButton label="Next page" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount}>
                <MdKeyboardArrowRight className="h-4 w-4" />
              </PagerButton>
              <PagerButton label="Last page" onClick={() => setPage(pageCount)} disabled={currentPage === pageCount}>
                <MdKeyboardDoubleArrowRight className="h-4 w-4" />
              </PagerButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HUmanResourceDashboard;
